import { formatDate } from '@angular/common';
import get from 'lodash/get';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';

import { defaultLocale, STORAGE_UNIT_SIZE } from '../app.constants';
import { FileOriginAction } from './enums/file.enum';
import { Device } from './models/device.model';
import { File, FileOrigin } from './models/file.model';
import { User } from './models/user.model';

export interface JoinOrigin {
  device: {
    [id: string]: Device
  }
  job: {
    [id: string]: any // eslint-disable-line @typescript-eslint/no-explicit-any
  }
  user: {
    [id: string]: User
  }
}

export interface OriginInfo {
  matIcon: string // for mat-icon
  description: string
  bidsDescription: string
  popoverText: string
}

interface Versioned {
  restored_by?: FileOrigin
  restored_from?: number
  version: number
}

function getBidsPopoverText(file: File): string[] {
  const bidsInfo = [];
  if (file?.parent?.label) {
    bidsInfo.push(`Label: ${file.parent.label}`);
  }
  if (file?.info?.BIDS?.Filename) {
    bidsInfo.push(`BIDS Filename: ${file.info.BIDS.Filename}`);
  }
  return bidsInfo;
}

export function getFileClassification(file: Pick<File, 'modality' | 'classification'>): string {
  const modality = file.modality || 'N/A';
  const classification = file.classification || {};
  const dimensions = (
    modality === 'MR'
      ? [].concat(
        classification['Intent'],
        classification['Measurement'],
        classification['Features'],
        classification['Custom'],
      )
      : [].concat(
        sortBy([].concat(...Object.values(omit(classification, 'Custom'))), value => value.toLowerCase()),
        classification['Custom'],
      )
  ).filter(Boolean);
  const uniqueDimensions = [];
  for (const dim of dimensions) {
    if (!uniqueDimensions.find(v => v.toLowerCase() === dim.toLowerCase())) {
      uniqueDimensions.push(dim);
    }
  }
  const displayDimensions = uniqueDimensions.join(', ');
  return modality + (displayDimensions && `: ${displayDimensions}`);
}

export function getOriginAction(versioned: Versioned, file?: File): FileOriginAction {
  if (versioned.restored_from) {
    return FileOriginAction.Restored;
  }
  if (versioned.version > 1) {
    return FileOriginAction.Replaced;
  }
  if (file?.copy_of) {
    return FileOriginAction.Copied;
  }
  return FileOriginAction.Created;
}

export function getOriginInfo(file: File, origins: JoinOrigin, bidsView = false): OriginInfo {
  const fileName = `File: ${file.name}`;
  const fileVersion = file.version ? `Version: ${file.version}` : '';
  const fileSize = `Size: ${(file.size / STORAGE_UNIT_SIZE / STORAGE_UNIT_SIZE).toFixed(2)} MB`;
  const zipFileCount = file.zip_member_count ? `File count in zip: ${file.zip_member_count}` : '';
  const fileBIDSDescription = get(file, 'info.BIDS.Filename', '');

  try {
    const originType = origins && origins[file.origin.type] || {};
    const origin = originType[file.origin.id] || { name: file.origin.name || file.origin.id };
    const avStatus = get(file, 'virus_scan.state') === 'quarantined' ? 'Status: Quarantined' : '';
    const bidsPopoverText = bidsView ? getBidsPopoverText(file) : [];
    switch (file.origin.type) {
      case 'job':
        return {
          matIcon: 'settings',
          description: file.name,
          bidsDescription: fileBIDSDescription,
          popoverText: [
            fileName,
            fileVersion,
            fileSize,
            origin.gear_info && origin.created ? `Initiated: ${formatDate(origin.created, 'short', defaultLocale)}` : '',
            origin.gear_info && origin.modified ? `Completed: ${formatDate(origin.modified, 'short', defaultLocale)}` : '',
            `Gear: ${origin.gear_info?.name || 'unknown'}`,
            origin.gear_info?.version ? `Gear Version: ${origin.gear_info.version}` : '',
            zipFileCount,
          ].filter(Boolean).join('\n'),
        };
      case 'device':
        return {
          matIcon: 'tablet_android',
          description: file.name,
          bidsDescription: fileBIDSDescription,
          popoverText: [
            ...bidsPopoverText,
            fileName,
            fileVersion,
            fileSize,
            `Uploaded: ${formatDate(file.created, 'short', defaultLocale)}`,
            file.replaced ? `Replaced: ${formatDate(file.replaced, 'short', defaultLocale)}` : null,
            `Device: ${origin.name}`,
            zipFileCount,
          ].filter(Boolean).join('\n'),
        };
      case 'user':
        return {
          matIcon: 'person',
          description: file.name,
          bidsDescription: fileBIDSDescription,
          popoverText: [
            ...bidsPopoverText,
            fileName,
            fileVersion,
            fileSize,
            `Uploaded: ${formatDate(file.created, 'short', defaultLocale)}`,
            file.replaced ? `Replaced: ${formatDate(file.replaced, 'short', defaultLocale)}` : null,
            `User: ${origin.firstname} ${origin.lastname}`,
            zipFileCount,
            avStatus,
          ].filter(Boolean).join('\n'),
        };
      default:
        return {
          matIcon: 'help_outline',
          description: file.name,
          bidsDescription: fileBIDSDescription,
          popoverText: '',
        };
    }
  } catch (error) {
    return {
      matIcon: '',
      description: '',
      bidsDescription: '',
      popoverText: '',
    };
  }
}

export function convertMbToBytes(mb: number): number {
  return mb * STORAGE_UNIT_SIZE * STORAGE_UNIT_SIZE;
}
